/* eslint-disable react/destructuring-assignment */
import { ActionIcon, Group, Paper, SimpleGrid, Tooltip } from '@mantine/core';
import { useModals } from '@mantine/modals';
import { IconEye, IconTimelineEventPlus, IconX } from '@tabler/icons-react';
import { ContractStatusEnum } from '../../../../../business/contracts/status';
import PageViewProperty from '../../../../../components/core/PageViewProperty/PageViewProperty';
import DeleteConfirmPopover from '../../../../../components/DeleteConfirmPopover';
import useCurrentUser from '../../../../../hooks/useCurrentUser';
import { ContractSearchResponseType } from '../../../../../models/core/contracts.type';
import { Permission } from '../../../../../models/core/departments.type';
import theme from '../../../../../theme';
import { validate } from '../../../../../utils/permission.utils';
import ContractExtendModalContent from '../../components/ContractExtendModalContent';

type DataViewActionProps = {
  item: ContractSearchResponseType;
  confirmActionResult(
    item: ContractSearchResponseType,
    action: string,
    confirmed: boolean,
    payload?: { dataFinal?: string }
  ): void;
};

export default function DataViewActions(props: DataViewActionProps) {
  const [currentUser] = useCurrentUser();
  const modals = useModals();

  const showModal = (item: ContractSearchResponseType, action: string) => {
    modals.openConfirmModal({
      title: `Gostaria de ${action} esse contrato?`,
      size: 'xl',
      closeOnClickOutside: false,
      closeOnEscape: false,
      children: (
        <Paper shadow="xs" p="md" withBorder>
          <SimpleGrid cols={3}>
            <PageViewProperty label="#" text={item.idContrato.toString()} size="sm" />
            <PageViewProperty label="CNPJ/CPF" text={item.clienteCNPJ || item.clienteCPF || '-'} size="sm" />
            <PageViewProperty
              label="Nome Fantasia/Nome"
              text={item.clienteNomeFantasia || item.clienteNome || '-'}
              size="sm"
            />
          </SimpleGrid>
        </Paper>
      ),
      onCancel: () => {
        props.confirmActionResult(item, action, false);
      },
      onConfirm: () => {
        props.confirmActionResult(item, action, true);
      },
    });
  };

  const showExtendModal = (item: ContractSearchResponseType) => {
    const modalId = modals.openModal({
      title: 'Gostaria de prorrogar esse contrato?',
      size: 'xl',
      closeOnClickOutside: false,
      closeOnEscape: false,
      children: (
        <ContractExtendModalContent
          item={item}
          onCancel={() => {
            modals.closeModal(modalId);
            props.confirmActionResult(item, 'prorrogar', false);
          }}
          onConfirm={(dataFinal) => {
            modals.closeModal(modalId);
            props.confirmActionResult(item, 'prorrogar', true, { dataFinal });
          }}
        />
      ),
    });
  };

  return (
    <Group>
      {validate(Permission.ContractView, currentUser.permissoes) && (
        <Tooltip withArrow transitionProps={{ transition: 'fade', duration: 200 }} label="Visualizar">
          <ActionIcon
            size="sm"
            radius="sm"
            color="primary"
            style={{ borderColor: theme?.colors?.primary?.[6] }}
            variant="outline"
            onClick={() => {
              props.confirmActionResult(props.item, 'visualizar', true);
            }}
          >
            <IconEye size={15} color={theme?.colors?.primary?.[6]} />
          </ActionIcon>
        </Tooltip>
      )}
      {[ContractStatusEnum.AguardandoInicio, ContractStatusEnum.EmAndamento].includes(
        props.item.codigoContratoStatus
      ) &&
        validate(Permission.ContractEdit, currentUser.permissoes) && (
          <Tooltip withArrow transitionProps={{ transition: 'fade', duration: 200 }} label="Prorrogar">
            <ActionIcon
              size="sm"
              radius="sm"
              color="blue"
              style={{ borderColor: 'blue' }}
              variant="outline"
              onClick={() => {
                showExtendModal(props.item);
              }}
            >
              <IconTimelineEventPlus size={15} color="blue" />
            </ActionIcon>
          </Tooltip>
        )}
      {props.item.codigoContratoStatus !== ContractStatusEnum.Cancelado &&
        props.item.codigoContratoStatus !== ContractStatusEnum.Prorrogando &&
        props.item.codigoContratoStatus !== ContractStatusEnum.EmSubstituicao &&
        props.item.codigoContratoStatus !== ContractStatusEnum.Substituido &&
        props.item.codigoContratoStatus !== ContractStatusEnum.Finalizado &&
        validate(Permission.ContractCancel, currentUser.permissoes) && (
          <Tooltip withArrow transitionProps={{ transition: 'fade', duration: 200 }} label="Cancelar">
            <ActionIcon
              size="sm"
              radius="sm"
              color="orange"
              style={{ borderColor: 'orange' }}
              variant="outline"
              onClick={() => {
                showModal(props.item, 'cancelar');
              }}
            >
              <IconX size={15} color="orange" />
            </ActionIcon>
          </Tooltip>
        )}
      {props.item.codigoContratoStatus === ContractStatusEnum.Cancelado &&
        validate(Permission.ContractDelete, currentUser.permissoes) && (
          <DeleteConfirmPopover
            label="Excluir"
            message="Esta ação removerá o contrato."
            variant="outline"
            onConfirm={() => {
              props.confirmActionResult(props.item, 'excluir', true);
            }}
            onCancel={() => {
              props.confirmActionResult(props.item, 'excluir', false);
            }}
          />
        )}
    </Group>
  );
}
